import { Delete } from '@mui/icons-material'
import CampaignIcon from '@mui/icons-material/Campaign'
import {
  Box,
  Chip,
  CircularProgress,
  DialogContentText,
  Link,
  Paper,
  Stack,
  Tab,
  Tabs,
  useTheme,
} from '@mui/material'
import { AxiosError } from 'axios'
import dayjs from 'dayjs'
import { useFormik } from 'formik'
import isEqual from 'lodash/isEqual'
import { nanoid } from 'nanoid'
import {
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react'
import {
  Outlet,
  Link as RouterLink,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'
import { toast } from 'react-toastify'

import AltGrowthButton from '~/components/altGrowthButton'
import ConfirmationDialog from '~/components/confirmationDialog'
import EditableTitle from '~/components/editableTitle'
import LoadingPaperSkeleton from '~/components/loadingPaperSkeleton'
import NotFound from '~/components/NotFound'
import { DEFAULT_TIMEZONE } from '~/constants'
import useGetAgentCentralized from '~/features/agents/hooks/useGetAgentCentralized'
import { EAgentKeys } from '~/features/agents/models'
import OFUCreateTemplateFromCampaign from '~/features/campaigns/components/ofuCreateTemplateFromCampaign'
import useArchiveCampaigns from '~/features/campaigns/hooks/useArchiveCampaigns'
import useCreateCampaign from '~/features/campaigns/hooks/useCreateCampaign'
import useGetCampaignById from '~/features/campaigns/hooks/useGetCampaignById'
import useUpdateCampaign from '~/features/campaigns/hooks/useUpdateCampaign'
import {
  ECampaignChannel,
  ECampaignKeys,
  ECampaignStatus,
  ECampaignTabs,
  ECampaignType,
  EOfuOutboundEmailProvider,
  TCampaign,
  TCampaignCreatePayload,
  TCampaignError,
  TCampaignUpdatePayload,
  TOFUCampaignFormFields,
} from '~/features/campaigns/models'
import {
  extractFriendlyErrorMessage,
  getMergeFields,
  getOutreachInstructionsDisplayText,
  getTabFromRoute,
  ofuCampaignValidationSchema,
  showToastOnCampaignErrorResponse,
} from '~/features/campaigns/utils'
import { prepareApiPayload } from '~/features/campaigns/utils/cloneCampaign'
import {
  CREATE_CAMPAIGN_DEFAULT_ID,
  DEFAULT_OFU_CAMPAIGN_NAME,
  OUTREACH_TIME_ASAP_VALUE,
} from '~/features/campaigns/utils/constants'
import { getParsedOfuOutreachTime } from '~/features/campaigns/utils/date'
import { TKnowledgeTableDataItem } from '~/features/knowledge/v1/models'
import { getChangedValues } from '~/features/settings/utils'
import { EClientPath } from '~/models/api'
import { sessionStore } from '~/store'
import pathBuilder from '~/utils/api'
import rem from '~/utils/ui'

function CreateEditOfuCampaignContainerLegacy() {
  const theme = useTheme()
  const location = useLocation()
  const { id } = useParams()
  const [urlSearchParams] = useSearchParams()
  const isCopy = urlSearchParams.get('isCopy') === 'true'
  const campaignType =
    (urlSearchParams.get('campaignType') as ECampaignType) || ECampaignType.ofu
  const navigate = useNavigate()
  const [tab, setTab] = useState(
    getTabFromRoute(location.pathname, id || CREATE_CAMPAIGN_DEFAULT_ID),
  )
  const [isArchiveCampaignDialogOpen, setIsArchiveCampaignDialogOpen] =
    useState(false)
  const agentId = sessionStore((state) => state.agent.id) || ''
  const { selectedAgent } = useGetAgentCentralized()

  const {
    data: { data: currentCampaignData } = {},
    isLoading,
    isError,
    error,
  } = useGetCampaignById((id !== CREATE_CAMPAIGN_DEFAULT_ID ? id : '') || '')

  const { mutate: createCampaign, isPending: isCreating } = useCreateCampaign()
  const { mutate: updateCampaign } = useUpdateCampaign(
    currentCampaignData?.id || '',
  )
  const isArchived =
    currentCampaignData?.status === ECampaignStatus.archived && !isCopy
  const { mutate: archiveCampaign, isPending: isDeleting } =
    useArchiveCampaigns(agentId, false)

  // Validate campaign type and redirect if needed
  useEffect(() => {
    if (
      currentCampaignData &&
      currentCampaignData.campaignType !== ECampaignType.ofu &&
      currentCampaignData.campaignType !== ECampaignType.ofuOneTime
    ) {
      navigate(
        pathBuilder(EClientPath.campaignCreateEdit, {
          id: currentCampaignData.id,
        }),
      )
    }
  }, [currentCampaignData, navigate])

  const handleSubmitCreate = (values: TOFUCampaignFormFields) => {
    const { id: unusedId, ...valuesWithoutId } = values

    const payload: TCampaignCreatePayload = {
      ...prepareApiPayload(
        valuesWithoutId,
        selectedAgent?.[EAgentKeys.agentTimezone] || DEFAULT_TIMEZONE,
      ),
      agentId,
    }

    createCampaign(payload, {
      onSuccess: (response) => {
        if ((response as TCampaignError).error) {
          toast.error(
            `Failed to create campaign. ${extractFriendlyErrorMessage(
              response as TCampaignError,
            )}`,
          )
          return
        }

        toast.success('Campaign created successfully')
        navigate({
          pathname: pathBuilder(EClientPath.campaignOfuContactsLegacy, {
            id: (response as TCampaign)?.id,
          }),
        })
      },
      onError: (response) =>
        showToastOnCampaignErrorResponse(response, 'Failed to create campaign'),
    })
  }

  const handleSubmitUpdate = (
    values: TOFUCampaignFormFields,
    initialValues: TOFUCampaignFormFields,
  ) => {
    const changedValues = getChangedValues(values, initialValues)
    const hasCustomBodyChanged = !isEqual(
      values[ECampaignKeys.ofuCustomEmailBody],
      initialValues[ECampaignKeys.ofuCustomEmailBody],
    )

    const payload = prepareApiPayload(
      {
        ...changedValues,
        ...(hasCustomBodyChanged
          ? {
              [ECampaignKeys.ofuCustomEmailBody]:
                values[ECampaignKeys.ofuCustomEmailBody],
            }
          : {}),
      },
      selectedAgent?.[EAgentKeys.agentTimezone] || DEFAULT_TIMEZONE,
    ) as TCampaignUpdatePayload

    updateCampaign(payload, {
      onSuccess: () => {
        toast.success('Campaign updated successfully')
      },
      onError: (response) =>
        showToastOnCampaignErrorResponse(response, 'Failed to update campaign'),
    })
  }

  const isCopyAndStartDateInThePast = useMemo(() => {
    const currentDate = new Date().toISOString()

    return (
      isCopy &&
      currentCampaignData?.startDate &&
      new Date(currentDate) > new Date(currentCampaignData.startDate)
    )
  }, [isCopy, currentCampaignData?.startDate])

  const campaignStartDate = useMemo(() => {
    const currentDate = new Date().toISOString()

    if (isCopyAndStartDateInThePast) {
      return currentDate
    }

    return currentCampaignData?.startDate || undefined
  }, [currentCampaignData?.startDate, isCopyAndStartDateInThePast])

  const campaignEndDate = useMemo<string | undefined>(() => {
    const currentDate = new Date().toISOString()

    if (
      isCopyAndStartDateInThePast &&
      currentCampaignData?.endDate &&
      currentCampaignData?.startDate
    ) {
      const diffInDays = dayjs(currentCampaignData.endDate).diff(
        dayjs(currentCampaignData.startDate),
        'day',
      )

      return dayjs(currentDate).add(diffInDays, 'day').toISOString()
    }

    return currentCampaignData?.endDate
  }, [
    currentCampaignData?.endDate,
    currentCampaignData?.startDate,
    isCopyAndStartDateInThePast,
  ])

  const getTimeOfFirstOutreach = () => {
    if (currentCampaignData?.ofu_outreachTime) {
      return getParsedOfuOutreachTime({
        agentTimezone: selectedAgent?.[EAgentKeys.agentTimezone],
        ofuOutreachTime: currentCampaignData.ofu_outreachTime,
      })
    }

    return OUTREACH_TIME_ASAP_VALUE
  }

  const formik = useFormik<TOFUCampaignFormFields>({
    initialValues: {
      [ECampaignKeys.id]: isCopy ? '' : currentCampaignData?.id || '',
      [ECampaignKeys.campaignName]: currentCampaignData?.campaignName
        ? `${currentCampaignData?.campaignName}${isCopy ? '-Copy' : ''}`
        : DEFAULT_OFU_CAMPAIGN_NAME,
      [ECampaignKeys.campaignType]:
        currentCampaignData?.campaignType || campaignType,
      [ECampaignKeys.mergeFields]: getMergeFields(
        currentCampaignData?.mergeFields || [],
        currentCampaignData?.ofu_channels &&
          currentCampaignData?.ofu_channels?.length > 0
          ? (currentCampaignData?.ofu_channels as ECampaignChannel[])
          : ECampaignChannel.phone,
      ),
      [ECampaignKeys.channel]:
        currentCampaignData?.channel || ECampaignChannel.phone,
      [ECampaignKeys.status]: isCopy
        ? ECampaignStatus.draft
        : currentCampaignData?.status || ECampaignStatus.draft,
      [ECampaignKeys.ofuGoal]: currentCampaignData?.ofu_goal || '',
      [ECampaignKeys.ofuQualificationCriteria]:
        currentCampaignData?.ofu_qualificationCriteria ||
        "Anyone who hasn't said no or wants to unsubscribe",
      [ECampaignKeys.startDate]: campaignStartDate,
      [ECampaignKeys.endDate]: campaignEndDate,
      [ECampaignKeys.ofuOutreachTime]: getTimeOfFirstOutreach(),
      [ECampaignKeys.ofuIsManual]: currentCampaignData?.ofu_isManual || false,
      [ECampaignKeys.ofuMaxReachouts]:
        currentCampaignData?.ofu_maxReachouts || 0,
      [ECampaignKeys.ofuCadence]: currentCampaignData?.ofu_cadence || '',
      [ECampaignKeys.ofuChannels]: currentCampaignData?.ofu_channels || [],
      [ECampaignKeys.ofuCadenceCustom]:
        currentCampaignData?.ofu_cadenceCustom || undefined,
      [ECampaignKeys.ofuCustomEmailService]:
        currentCampaignData?.ofu_customEmailService || undefined,
      [ECampaignKeys.ofuCustomEmailBody]:
        currentCampaignData?.ofu_customEmailBody || undefined,
      [ECampaignKeys.outreachInstructionsText]:
        getOutreachInstructionsDisplayText(currentCampaignData ?? {}),
      [ECampaignKeys.campaignFacts]: !currentCampaignData?.campaignFacts
        ? []
        : currentCampaignData?.campaignFacts.map(
            (fact) =>
              ({
                id: fact.id || nanoid(),
                fact: fact.text,
              }) as TKnowledgeTableDataItem,
          ),
      [ECampaignKeys.useAgentFacts]:
        currentCampaignData?.useAgentFacts != null
          ? currentCampaignData?.useAgentFacts
          : true,
      [ECampaignKeys.ofuOutboundEmailProvider]:
        currentCampaignData?.ofu_outboundEmailProvider ||
        EOfuOutboundEmailProvider.internal,
      [ECampaignKeys.ofuOutboundEmailProviderId]:
        currentCampaignData?.ofu_outboundEmailProviderId || undefined,
      [ECampaignKeys.introMessage]: currentCampaignData?.introMessage || '',
      [ECampaignKeys.voicemailMessage]:
        currentCampaignData?.voicemailMessage || '',
      [ECampaignKeys.firstEmailBody]: currentCampaignData?.firstEmailBody || '',
      [ECampaignKeys.firstEmailSubject]:
        currentCampaignData?.firstEmailSubject || '',
      [ECampaignKeys.firstTextMessage]:
        currentCampaignData?.firstTextMessage || '',
      [ECampaignKeys.ofu_firstOutreachChannel]:
        currentCampaignData?.ofu_firstOutreachChannel || undefined,
    },
    enableReinitialize: true,
    validationSchema: ofuCampaignValidationSchema,
    onSubmit: (values) => {
      if (!formik.dirty && !isCopy) {
        return
      }

      if (isCopy || id === CREATE_CAMPAIGN_DEFAULT_ID) {
        handleSubmitCreate(values)
      } else {
        handleSubmitUpdate(values, formik.initialValues)
      }
    },
  })

  useLayoutEffect(() => {
    if (isCopy && currentCampaignData && !isLoading) {
      setTimeout(() => {
        formik.submitForm()
      })
    }
  }, [isCopy, formik.submitForm, currentCampaignData, isLoading])

  const handleTabChange = useCallback(
    (_: React.SyntheticEvent | null, newTab: ECampaignTabs) => {
      let pathByTab

      switch (newTab) {
        case ECampaignTabs.contacts:
          pathByTab = pathBuilder(EClientPath.campaignOfuContactsLegacy, {
            id: isCopy || !id ? CREATE_CAMPAIGN_DEFAULT_ID : id,
          })
          break
        case ECampaignTabs.results:
          pathByTab = pathBuilder(EClientPath.campaignOfuResultsLegacy, {
            id: isCopy || !id ? CREATE_CAMPAIGN_DEFAULT_ID : id,
          })
          break
        case ECampaignTabs.details:
        default:
          pathByTab = pathBuilder(EClientPath.campaignOfuDetailsLegacy, {
            id: isCopy || !id ? CREATE_CAMPAIGN_DEFAULT_ID : id,
          })
          break
      }

      setTab(newTab)
      navigate(pathByTab)
    },
    [id, navigate, isCopy],
  )

  // Sync tab state from URL (e.g. browser back/forward). Only update when out of sync
  // to avoid calling handleTabChange -> navigate() in a loop when already on that tab.
  const tabFromRoute = getTabFromRoute(
    location.pathname,
    isCopy || !id ? CREATE_CAMPAIGN_DEFAULT_ID : id,
  )

  useEffect(() => {
    if (tabFromRoute !== tab) {
      setTab(tabFromRoute)
    }
  }, [tabFromRoute, tab])

  useEffect(() => {
    if (formik.values.ofu_channels) {
      const newMergeFields = getMergeFields(
        formik.values.mergeFields,
        formik.values.ofu_channels as ECampaignChannel[],
      )

      formik.setFieldValue(ECampaignKeys.mergeFields, newMergeFields)
    }
  }, [formik.values.ofu_channels])

  const shouldDisplayNotFound =
    isError &&
    (error as AxiosError)?.response?.status === 404 &&
    (error as AxiosError<{ message: string }>)?.response?.data?.message ===
      'Campaign not found'

  if (isError && !shouldDisplayNotFound) {
    toast.error('Error loading campaign')
    navigate(EClientPath.campaignList)
    return null
  }

  const handleArchiveCampaign = () => {
    archiveCampaign([{ id: currentCampaignData?.id || '', agentId }], {
      onSuccess: () => {
        toast.success('Campaign archived successfully')
        setIsArchiveCampaignDialogOpen(false)
      },
      onError: () => {
        toast.error('Failed to archive campaign. Please try again.')
      },
    })
  }

  const handleArchiveCampaignClick = () => {
    setIsArchiveCampaignDialogOpen(true)
  }

  const handleCancelArchiveClick = () => {
    setIsArchiveCampaignDialogOpen(false)
  }

  return (
    <>
      <Link
        to={EClientPath.campaignList}
        component={RouterLink}
        sx={{
          width: 'fit-content',
          mb: 2,
        }}
      >
        View all campaigns
      </Link>
      {isError && shouldDisplayNotFound && (
        <NotFound message="No campaign with that Id exists on this assistant." />
      )}
      {!isError && (
        <form onSubmit={formik.handleSubmit}>
          <Stack
            spacing={2}
            direction="row"
            sx={{ alignItems: 'center' }}
            marginBottom={2}
          >
            <CampaignIcon fontSize="large" />
            {isLoading ? (
              <LoadingPaperSkeleton sx={{ height: '2.9rem' }} />
            ) : (
              <Stack
                direction="row"
                justifyContent="space-between"
                sx={{ width: '100%' }}
                alignItems="center"
              >
                <Stack direction="row" spacing={4} alignItems="center">
                  <EditableTitle
                    name={ECampaignKeys.campaignName}
                    ariaLabel="Campaign name"
                    value={formik.values.campaignName}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.campaignName &&
                      Boolean(formik.errors.campaignName)
                    }
                    helperText={
                      formik.touched.campaignName
                        ? formik.errors.campaignName
                        : ''
                    }
                    onSubmit={() =>
                      !isCopy &&
                      id !== CREATE_CAMPAIGN_DEFAULT_ID &&
                      updateCampaign({
                        campaignName: formik.values.campaignName,
                      })
                    }
                    disabled={formik.values.status === ECampaignStatus.archived}
                  />
                  {formik.values.status === ECampaignStatus.archived && (
                    <Chip label="Archived" color="error" />
                  )}
                </Stack>
                {!isCopy && id !== CREATE_CAMPAIGN_DEFAULT_ID && (
                  <Stack gap={2}>
                    {!isArchived && (
                      <AltGrowthButton
                        onClick={handleArchiveCampaignClick}
                        disabled={isDeleting}
                        color="secondary"
                        customSx={{ width: '180px' }}
                      >
                        {!isDeleting && (
                          <Stack>
                            <Delete sx={{ fontSize: rem(20), mr: 2 }} />
                          </Stack>
                        )}
                        {isDeleting && (
                          <CircularProgress
                            size={rem(20)}
                            color="inherit"
                            sx={{ mr: 2 }}
                          />
                        )}
                        Archive
                      </AltGrowthButton>
                    )}
                    <OFUCreateTemplateFromCampaign />
                  </Stack>
                )}
              </Stack>
            )}
          </Stack>
          <Box sx={{ width: '100%' }}>
            <Box>
              <Tabs
                value={tab}
                onChange={handleTabChange}
                aria-label="campaign tabs"
                sx={{
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontSize: rem(18),
                    color: theme.palette.secondaryButtonFont,
                    width: { xxs: rem(114), xs: rem(114), md: rem(170) },
                    height: rem(60),
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: 'transparent',
                  },
                  '& .MuiButtonBase-root.MuiTab-root.Mui-selected': {
                    color: theme.palette.secondaryButtonFont,
                    background: theme.palette.common.white,
                  },
                }}
              >
                <Tab label="Setup" />
                <Tab label="Manage" disabled={!currentCampaignData?.id} />
                <Tab label="Reports" disabled={!currentCampaignData?.id} />
              </Tabs>
            </Box>
            <Paper>
              {isLoading || isCreating || !formik ? (
                <LoadingPaperSkeleton sx={{ height: '70vh' }} />
              ) : (
                <Suspense fallback={<LoadingPaperSkeleton />}>
                  {formik && <Outlet context={{ formik }} />}
                </Suspense>
              )}
            </Paper>
          </Box>
        </form>
      )}
      <ConfirmationDialog
        title="Archive Campaign"
        open={isArchiveCampaignDialogOpen}
        handleCancel={handleCancelArchiveClick}
        handleConfirm={handleArchiveCampaign}
        isWarning
        isDisabled={isDeleting}
      >
        <DialogContentText>
          Are you sure you want to archive {formik.values.campaignName}?
        </DialogContentText>
      </ConfirmationDialog>
    </>
  )
}

export default CreateEditOfuCampaignContainerLegacy
